import {
  updateCheckoutBuyer,
  updateCheckoutReceiver,
} from "@server/requests/checkout";
import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Dictionary } from "types";

import { isAdyenCheckoutEnabled } from "@/config/checkout";
import type { MarketConfiguration } from "@/config/markets/const";

import { checkoutStore } from "../Checkout.store";
import {
  getAddressSchema,
  getDeliveryAddressSchema,
  getZipCodeOnlySchema,
  superRefinePhoneNumber,
  transformPhoneNumber,
} from "./Address.utils";

export function useUpdateCheckoutBuyer() {
  const zipCode = checkoutStore((state) => state.address.zipCode);

  return useMutation({
    mutationFn: updateCheckoutBuyer,
    mutationKey: ["updateCheckoutBuyer", zipCode],
  });
}

export function useUpdateCheckoutReciver() {
  const zipCode = checkoutStore(
    (state) => state.deliveryAddress.deliveryZipCode,
  );

  return useMutation({
    mutationFn: updateCheckoutReceiver,
    mutationKey: ["updateCheckoutReciver", zipCode],
  });
}

export function useMergedAddressSchema({
  dictionary,
  marketConfig,
}: {
  dictionary: Dictionary;
  marketConfig: MarketConfiguration;
}) {
  const { code: marketCode } = marketConfig;
  const zipCodeOnlySchema = getZipCodeOnlySchema({ marketCode, dictionary });
  const addressSchema = getAddressSchema({ marketCode, dictionary });
  const deliveryAddressSchema = getDeliveryAddressSchema({
    marketCode,
    dictionary,
  });

  const hasSeparateDeliveryAddress = checkoutStore(
    (state) => state.hasSeparateDeliveryAddress,
  );

  const mergedAddressSchema = useMemo(() => {
    if (isAdyenCheckoutEnabled({ marketConfig }) === false) {
      return zipCodeOnlySchema;
    }

    // Merge billing address and delivery address schema if ship to different address is enabled.
    // By doing this, we can validate both billing and shipping address at the same time.
    if (hasSeparateDeliveryAddress) {
      return addressSchema
        .merge(deliveryAddressSchema)
        .superRefine((data, ctx) => {
          superRefinePhoneNumber(data, ctx, {
            dictionary,
            marketCode,
          });
        })
        .transform(transformPhoneNumber);
    }

    // Otherwise, return address schema i.e. the billing address schema only.
    // We can then expect that billing and shipping address are the same.
    return addressSchema
      .superRefine((data, ctx) => {
        superRefinePhoneNumber(data, ctx, {
          dictionary,
          marketCode,
        });
      })
      .transform(transformPhoneNumber);
  }, [
    marketConfig,
    hasSeparateDeliveryAddress,
    addressSchema,
    zipCodeOnlySchema,
    deliveryAddressSchema,
    dictionary,
    marketCode,
  ]);

  return mergedAddressSchema;
}
